import { PopoverOrigin } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

interface UseContextMenuOptions {
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  openAtCursor?: boolean;
}

export interface ContextMenuCustomEvent<TElement> {
  clientX: number;
  clientY: number;
  currentTarget: TElement;
}

export const useContextMenu = <TElement extends Element = HTMLElement>(
  options: UseContextMenuOptions = {
    anchorOrigin: {
      vertical: 'center',
      horizontal: 'center',
    },
    transformOrigin: { vertical: 'top', horizontal: 'right' },
    openAtCursor: false,
  }
) => {
  const { anchorOrigin, transformOrigin, openAtCursor } = options;
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);
  const [anchorEl, setAnchorEl] = useState<TElement | null>(null);

  const handleOpenMenu = useCallback(
    (event: React.MouseEvent<TElement> | ContextMenuCustomEvent<TElement>) => {
      setMousePosition({ x: event.clientX - 2, y: event.clientY - 4 });
      setAnchorEl(event.currentTarget);
    },
    []
  );

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
    setMousePosition(null);
  }, []);

  const positionProps = useMemo(() => {
    let newPositionProps = {};
    if (!openAtCursor) {
      newPositionProps = {
        anchorOrigin,
        transformOrigin,
      };
    } else {
      newPositionProps = {
        anchorReference: 'anchorPosition',
        anchorPosition: { top: mousePosition?.y || 0, left: mousePosition?.x || 0 },
      };
    }
    return newPositionProps;
  }, [anchorOrigin, mousePosition?.x, mousePosition?.y, openAtCursor, transformOrigin]);

  const props = useMemo(() => {
    return {
      onClose: handleCloseMenu,
      anchorEl: anchorEl,
      open: Boolean(anchorEl),
      getContentAnchorEl: null,
      ...positionProps,
    };
  }, [anchorEl, handleCloseMenu, positionProps]);

  return {
    anchorEl: anchorEl as null | (TElement & { dataset: { id: string } }),
    handleOpenMenu,
    handleCloseMenu,
    props,
  };
};
